import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, PartyPopper } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SLIDES = [
  {
    key: "intro",
    image: "/tutorial/01-home.jpg",
    title: "Benvenuto su Hopzigo",
    body: "Hopzigo è l'app che permette ai genitori di un gruppo chiuso di condividere corse in auto per i propri figli. Niente sconosciuti: solo le famiglie che conosci.",
    gradient: "linear-gradient(135deg, #1A2A6C 0%, #2D3F8F 100%)",
    accent: "#FFD24A",
  },
  {
    key: "group",
    image: "/tutorial/02-group.jpg",
    title: "Crea o entra in un gruppo",
    body: "Crea un gruppo per la classe, lo sport o la scuola e invita le altre famiglie con un codice o un link. Solo chi accetti potrà vedere e prenotare le corse.",
    gradient: "linear-gradient(135deg, #26B5A8 0%, #1F968B 100%)",
    accent: "#FFE9C2",
  },
  {
    key: "places",
    image: "/tutorial/03-places.jpg",
    title: "Salva i tuoi punti di interesse",
    body: "Aggiungi i luoghi che usi spesso (Casa, Scuola, Palestra…) con il loro indirizzo. Saranno suggeriti automaticamente quando crei una corsa: niente più digitazione ripetitiva.",
    gradient: "linear-gradient(135deg, #FFC940 0%, #E0A012 100%)",
    accent: "#1A2A6C",
  },
  {
    key: "ride",
    image: "/tutorial/04-newride.jpg",
    title: "Crea una corsa",
    body: "Offri una corsa indicando data, ora, partenza, destinazione e posti disponibili. Oppure pubblica una richiesta se ti serve una corsa per tuo figlio.",
    gradient: "linear-gradient(135deg, #FF9E1B 0%, #E07A00 100%)",
    accent: "#FFFFFF",
  },
  {
    key: "accept",
    image: "/tutorial/05-rides.jpg",
    title: "Prenota un posto",
    body: "Sfoglia le corse disponibili nel tuo gruppo e prenota un posto in auto inserendo il nome del bambino che parteciperà. Il guidatore riceve subito una notifica.",
    gradient: "linear-gradient(135deg, #FF4D5E 0%, #C73846 100%)",
    accent: "#FFE9C2",
  },
  {
    key: "confirm",
    image: "/tutorial/06-detail.jpg",
    title: "Conferma e notifica",
    body: "Il guidatore conferma la tua prenotazione con un tap. Tu ricevi una notifica push: tutti i dettagli e la chat della corsa sono sempre a portata di mano.",
    gradient: "linear-gradient(135deg, #6C5CE7 0%, #4B3FB3 100%)",
    accent: "#FFD24A",
  },
  {
    key: "done",
    icon: PartyPopper,
    title: "Il gioco è fatto!",
    body: "Ora sei pronto: meno traffico, meno stress, più tempo per la famiglia. Inizia subito creando il tuo primo gruppo o accettando un invito.",
    gradient: "linear-gradient(135deg, #1A2A6C 0%, #26B5A8 100%)",
    accent: "#FFD24A",
  },
];

export default function OnboardingTutorial({ onClose }) {
  const { setUser } = useAuth();
  const [index, setIndex] = useState(0);
  const [completing, setCompleting] = useState(false);
  const touchStart = useRef(null);
  const isLast = index === SLIDES.length - 1;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const next = () => index < SLIDES.length - 1 && setIndex(index + 1);
  const prev = () => index > 0 && setIndex(index - 1);

  const finish = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      const { data } = await api.post("/auth/tutorial-completed");
      if (data?.user) setUser(data.user);
    } catch {
      /* ignore */
    } finally {
      setCompleting(false);
      if (onClose) onClose();
    }
  };

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) next();
    else prev();
  };

  const slide = SLIDES[index];
  const Icon = slide.icon;

  const node = (
    <div
      className="fixed inset-0 z-[100] flex flex-col"
      style={{ background: slide.gradient, transition: "background 400ms ease" }}
      data-testid="onboarding-tutorial-overlay"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 pt-5 sm:pt-6 shrink-0">
        <div className="flex items-center gap-1.5" data-testid="tutorial-progress-dots">
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setIndex(i)}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === index ? 28 : 10,
                backgroundColor: i <= index ? "#FFFFFF" : "rgba(255,255,255,0.35)",
              }}
              aria-label={`Vai allo step ${i + 1}`}
              data-testid={`tutorial-dot-${i}`}
            />
          ))}
        </div>
        <button
          onClick={finish}
          className="text-white/90 hover:text-white text-sm font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-xl hover:bg-white/10"
          data-testid="tutorial-skip"
        >
          Salta <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content - flex column with image area + text area */}
      <div
        className="flex-1 flex flex-col items-center justify-start px-6 pt-4 pb-2 overflow-y-auto"
        key={slide.key}
      >
        {/* Phone mockup OR icon */}
        {slide.image ? (
          <div
            className="relative anim-pop-in mx-auto"
            style={{
              width: "min(78vw, 260px)",
              aspectRatio: "9 / 18",
              maxHeight: "52vh",
            }}
          >
            <div className="absolute inset-0 rounded-[36px] bg-black p-[6px] shadow-[0_28px_60px_rgba(0,0,0,0.45)]">
              <div className="relative w-full h-full rounded-[30px] overflow-hidden bg-white">
                {/* Notch */}
                <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-20 h-4 rounded-full bg-black z-10" />
                <img
                  src={slide.image}
                  alt=""
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                  data-testid={`tutorial-image-${slide.key}`}
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            className="w-32 h-32 sm:w-36 sm:h-36 grid place-items-center rounded-3xl shadow-[0_18px_40px_rgba(0,0,0,0.25)] mt-4 anim-pop-in"
            style={{ backgroundColor: slide.accent }}
          >
            <Icon className="w-16 h-16 sm:w-20 sm:h-20" style={{ color: "#1A1D29" }} strokeWidth={2} />
          </div>
        )}

        {/* Text */}
        <div className="max-w-md w-full text-center text-white mt-5">
          <p
            className="text-[11px] font-bold tracking-[0.25em] uppercase mb-2"
            style={{ color: slide.accent }}
            data-testid="tutorial-step-label"
          >
            Step {index + 1} di {SLIDES.length}
          </p>
          <h2
            className="font-heading text-2xl sm:text-3xl font-black leading-tight mb-2"
            data-testid="tutorial-step-title"
          >
            {slide.title}
          </h2>
          <p className="text-sm sm:text-base text-white/85 leading-relaxed" data-testid="tutorial-step-body">
            {slide.body}
          </p>
        </div>
      </div>

      {/* Bottom controls */}
      <div className="px-5 pb-6 sm:pb-9 pt-3 shrink-0">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <button
            onClick={prev}
            disabled={index === 0}
            className="w-12 h-12 grid place-items-center rounded-2xl bg-white/15 border border-white/25 text-white backdrop-blur disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="tutorial-prev"
            aria-label="Indietro"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          {isLast ? (
            <button
              onClick={finish}
              disabled={completing}
              className="flex-1 h-12 rounded-2xl bg-white text-[#1A2A6C] font-black inline-flex items-center justify-center gap-2 shadow-[0_8px_24px_rgba(0,0,0,0.22)] hover:bg-[#F5F6FA] disabled:opacity-70"
              data-testid="tutorial-finish"
            >
              Inizia ad usare Hopzigo <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={next}
              className="flex-1 h-12 rounded-2xl bg-white text-[#1A2A6C] font-black inline-flex items-center justify-center gap-2 shadow-[0_8px_24px_rgba(0,0,0,0.22)] hover:bg-[#F5F6FA]"
              data-testid="tutorial-next"
            >
              Avanti <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
